import { GrooveShuffleBox } from "@opendaw/studio-boxes";
import { asInstanceOf, UUID } from "@opendaw/lib-std";
import { migrateAudioClipBox, migrateAudioFileBox, migrateAudioRegionBox, migrateAudioRegionOverlaps, migrateAudioUnitBox, migrateCaptureTrackMismatch, migrateDelayDeviceBox, migrateMIDIOutputDeviceBox, migrateNeuralAmpDeviceBox, migrateRevampDeviceBox, migrateSelectionBox, migrateTimelineBox, migrateValueEventBox, migrateValueEventCollection, migrateVaporisateurDeviceBox, migrateWarpMarkers, migrateZeitgeistDeviceBox, migrateZeroDurationRegions } from "./migration";
export class ProjectMigration {
    static async migrate(env, { boxGraph, mandatoryBoxes }) {
        const { rootBox, timelineBox: { bpm } } = mandatoryBoxes;
        console.debug("migrate project from", rootBox.created.getValue());
        if (rootBox.groove.targetAddress.isEmpty()) {
            console.debug("Migrate to global GrooveShuffleBox");
            boxGraph.beginTransaction();
            rootBox.groove.refer(GrooveShuffleBox.create(boxGraph, UUID.generate()));
            boxGraph.endTransaction();
        }
        const globalShuffle = asInstanceOf(rootBox.groove.targetVertex.unwrap("groove.target"), GrooveShuffleBox).label;
        if (globalShuffle.getValue() !== "Groove Shuffle") {
            boxGraph.beginTransaction();
            globalShuffle.setValue("Groove Shuffle");
            boxGraph.endTransaction();
        }
        const loadAudioData = (uuid) => {
            const { promise, resolve, reject } = Promise.withResolvers();
            const loader = env.sampleManager.getOrCreate(uuid);
            let subscription;
            subscription = loader.subscribe(state => {
                if (state.type === "loaded") {
                    queueMicrotask(() => subscription.terminate());
                    resolve(loader.data.unwrap("State mismatch"));
                }
                else if (state.type === "error") {
                    queueMicrotask(() => subscription.terminate());
                    reject(new Error(state.reason));
                }
            });
            return promise;
        };
        const orphans = boxGraph.findOrphans(rootBox);
        if (orphans.length > 0) {
            console.debug("Migrate remove orphaned boxes: ", orphans.length);
            boxGraph.beginTransaction();
            orphans.forEach(orphan => orphan.delete());
            boxGraph.endTransaction();
        }
        const grooveTarget = rootBox.groove.targetVertex.unwrap("groove.target");
        const outputMidiDevices = rootBox.outputMidiDevices;
        const bpmValue = bpm.getValue();
        // 1st pass (2nd pass might rely on those changes)
        for (const box of boxGraph.boxes()) {
            await box.accept({
                visitAudioFileBox: (box) => migrateAudioFileBox(boxGraph, box, loadAudioData),
                visitNeuralAmpDeviceBox: (box) => migrateNeuralAmpDeviceBox(boxGraph, box)
            });
        }
        // 2nd pass. We need to run on a copy, because we might add more boxes during the migration
        boxGraph.boxes().slice().forEach(box => box.accept({
            visitAudioRegionBox: (box) => migrateAudioRegionBox(boxGraph, box, bpmValue),
            visitAudioClipBox: (box) => migrateAudioClipBox(boxGraph, box),
            visitAudioPitchStretchBox: (box) => migrateWarpMarkers(boxGraph, box),
            visitAudioTimeStretchBox: (box) => migrateWarpMarkers(boxGraph, box),
            visitTimelineBox: (box) => migrateTimelineBox(boxGraph, box),
            visitMIDIOutputDeviceBox: (box) => migrateMIDIOutputDeviceBox(boxGraph, box, outputMidiDevices),
            visitZeitgeistDeviceBox: (box) => migrateZeitgeistDeviceBox(boxGraph, box, grooveTarget),
            visitValueEventBox: (box) => migrateValueEventBox(boxGraph, box),
            visitAudioUnitBox: (box) => migrateAudioUnitBox(boxGraph, box),
            visitRevampDeviceBox: (box) => migrateRevampDeviceBox(boxGraph, box),
            visitVaporisateurDeviceBox: (box) => migrateVaporisateurDeviceBox(boxGraph, box),
            visitValueEventCollectionBox: (box) => migrateValueEventCollection(boxGraph, box),
            visitDelayDeviceBox: (box) => migrateDelayDeviceBox(boxGraph, box),
            visitSelectionBox: (box) => migrateSelectionBox(boxGraph, box)
        }));
        // 3rd pass. Drop content tracks whose type no longer matches their unit's capture device (a MIDI
        // instrument swapped for a Tape leaves note tracks on an audio-capture unit, and vice versa) — they
        // are unusable and crash editors. Runs after per-unit migration, which ensures each unit has a
        // capture box, so the comparison reflects the current instrument.
        migrateCaptureTrackMismatch(boxGraph);
        // 4th pass. Drop regions with a non-positive (derived) duration — legacy of the zero-length-sample
        // bug — so they can never trip validateTrack on a later edit. Runs after per-region migration (which
        // can rewrite audio durations) and before the overlap heal (which then sees only valid spans).
        migrateZeroDurationRegions(boxGraph, bpmValue);
        // 5th pass. Heal sub-ppqn overlaps that the Int32 position truncation (or the AudioFit->Seconds
        // pass above) left between seconds-based audio regions. Runs after per-region migration.
        migrateAudioRegionOverlaps(boxGraph, bpmValue);
    }
}
